import React, { useRef, useState } from "react";
import styled from "styled-components";

interface FileUploadProps {
  id?: string;
  accept?: string;
  file: File | null;
  onChange: (file: File | null) => void;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  loading?: boolean; // Thêm dòng này
}

const FileUploadContainer = styled.div<{ fullWidth?: boolean }>`
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  position: relative;
`;

const FileUploadButton = styled.button<{ hasFile: boolean; isDragActive: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  background: ${({ theme, isDragActive }) =>
    isDragActive ? "#e7f5ff" : theme.colors.surface};
  border: 2px dashed ${({ theme, isDragActive }) =>
    isDragActive ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme, hasFile }) =>
    hasFile ? theme.colors.textPrimary : theme.colors.textSecondary};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const FileIcon = styled.svg`
  width: 1.25rem;
  height: 1.25rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FileName = styled.span`
  font-weight: 500;
`;

const FileSize = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ClearButton = styled.button`
  padding: 0.25rem;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover {
    color: ${({ theme }) => theme.colors.error};
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:focus {
    outline: none;
    color: ${({ theme }) => theme.colors.error};
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  z-index: 2;
`;

const LoadingSpinner = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid transparent;
  border-top: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const checkFileType = (file: File, accept?: string) => {
  if (!accept) return true;
  const exts = accept.split(",").map(ext => ext.trim().replace(".", ""));
  return exts.some(ext => file.name.toLowerCase().endsWith(`.${ext}`));
};

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  accept,
  file,
  onChange,
  placeholder = "ファイルを選択してください",
  disabled = false,
  fullWidth = false,
  loading = false, // Thêm dòng này
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleButtonClick = () => {
    if (!disabled && !loading && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile && !checkFileType(selectedFile, accept)) {
      window.alert(`対応ファイル形式のみアップロード可能です: ${accept}`);
      return;
    }
    onChange(selectedFile);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleButtonClick();
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && !loading) setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (disabled || loading) return;
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!checkFileType(droppedFile, accept)) {
        window.alert(`対応ファイル形式のみアップロード可能です: ${accept}`);
        return;
      }
      onChange(droppedFile);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <FileUploadContainer fullWidth={fullWidth}>
      <FileUploadButton
        type="button"
        hasFile={!!file}
        isDragActive={isDragActive}
        disabled={disabled || loading}
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        aria-label={file ? `Selected file: ${file.name}` : placeholder}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {file ? (
          <FileInfo>
            <FileIcon viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </FileIcon>
            <div>
              <FileName>{file.name}</FileName>
              <FileSize> ({formatFileSize(file.size)})</FileSize>
            </div>
          </FileInfo>
        ) : (
          <FileInfo>
            <FileIcon viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </FileIcon>
            <span>
              {placeholder}
              <br />
              <span style={{ fontSize: "0.8em", color: "#339af0" }}>
                ここにファイルをドラッグ＆ドロップできます
              </span>
            </span>
          </FileInfo>
        )}

        {file && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label="Clear selected file"
            disabled={disabled || loading}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M12.854 3.146a.5.5 0 0 1 0 .708L8.707 8l4.147 4.146a.5.5 0 0 1-.708.708L8 8.707l-4.146 4.147a.5.5 0 0 1-.708-.708L7.293 8 3.146 3.854a.5.5 0 1 1 .708-.708L8 7.293l4.146-4.147a.5.5 0 0 1 .708 0z"/>
            </svg>
          </ClearButton>
        )}
      </FileUploadButton>

      <HiddenInput
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        disabled={disabled || loading}
      />

      {loading && (
        <LoadingOverlay aria-hidden="true">
          <LoadingSpinner />
        </LoadingOverlay>
      )}
    </FileUploadContainer>
  );
};

export default FileUpload;